import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  ArrowBack,
  ArrowForward,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as DocumentIcon,
  Home as PropertyIcon,
  Person as TenantIcon,
  CalendarMonth,
  AttachMoney,
  Schedule,
  Warning as WarningIcon,
  CheckCircle,
  Cancel,
  Refresh as RenewalIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { contractsApi } from '../../../lib/api';
import { useApi } from '../../../hooks/useApi';
import { useAppStore } from '../../../lib/store';
import { format, differenceInDays, isPast } from 'date-fns';
import { ar, enUS } from 'date-fns/locale';

type ContractStatus = 'active' | 'expired' | 'terminated' | 'draft' | 'renewal';

export default function ContractDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['contracts', 'common']);
  const language = useAppStore((state) => state.settings.language);
  const isRTL = language === 'ar';
  const dateLocale = isRTL ? ar : enUS;

  const [terminateDialog, setTerminateDialog] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Fetch contract details
  const {
    data: contract,
    loading,
    error,
    refetch,
  } = useApi(() => (id ? contractsApi.getById(id) : Promise.resolve({ data: null, error: null })), [id]);

  const handleTerminate = async () => {
    if (!id) return;

    try {
      const result = await contractsApi.terminate(id);
      if (result.error) {
        setErrorMessage(result.error.message);
      } else {
        setSuccessMessage(t('contractTerminated'));
        setTerminateDialog(false);
        refetch();
      }
    } catch (err) {
      setErrorMessage(t('common:error'));
    }
  };

  const getStatusColor = (status: ContractStatus) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'expired':
        return 'error';
      case 'terminated':
        return 'error';
      case 'draft':
        return 'default';
      case 'renewal':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: ContractStatus) => {
    switch (status) {
      case 'active':
        return <CheckCircle />;
      case 'expired':
        return <WarningIcon />;
      case 'terminated':
        return <Cancel />;
      case 'renewal':
        return <RenewalIcon />;
      default:
        return null;
    }
  };

  const formatDate = (date: string | Date | null) => {
    if (!date) return '-';
    return format(new Date(date), 'PPP', { locale: dateLocale });
  };

  const formatCurrency = (amount: number | string | null) => {
    if (amount === null || amount === undefined) return '-';
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    // Always use English numerals (en-SA locale)
    return new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: 'SAR',
    }).format(num);
  };

  const getPaymentFrequencyLabel = (frequency: string) => {
    switch (frequency) {
      case 'monthly':
        return t('frequencyMonthly');
      case 'quarterly':
        return t('frequencyQuarterly');
      case 'biannually':
        return t('frequencyBiannually');
      case 'annually':
        return t('frequencyAnnually');
      default:
        return frequency;
    }
  };

  const getDaysRemaining = () => {
    if (!contract?.end_date) return null;
    const endDate = new Date(contract.end_date);
    if (isPast(endDate)) return null;
    return differenceInDays(endDate, new Date());
  };

  const daysRemaining = getDaysRemaining();
  const isExpiringSoon = daysRemaining !== null && daysRemaining <= 30 && daysRemaining > 0;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !contract) {
    return (
      <Box p={3}>
        <Alert severity="error">{error?.message || t('common:error')}</Alert>
        <Button
          startIcon={isRTL ? <ArrowForward /> : <ArrowBack />}
          onClick={() => navigate('/dashboard/contracts')}
          sx={{ mt: 2 }}
        >
          {t('common:back')}
        </Button>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box display="flex" alignItems="center" gap={2}>
          <Button
            startIcon={isRTL ? <ArrowForward /> : <ArrowBack />}
            onClick={() => navigate('/dashboard/contracts')}
          >
            {t('common:back')}
          </Button>
          <Typography variant="h5" fontWeight="bold">
            {t('contractDetails')}
          </Typography>
          <Chip
            icon={getStatusIcon(contract.status as ContractStatus)}
            label={t(`status${contract.status.charAt(0).toUpperCase() + contract.status.slice(1)}`)}
            color={getStatusColor(contract.status as ContractStatus)}
            size="medium"
          />
        </Box>
        <Box display="flex" gap={1}>
          <Button
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={() => navigate(`/dashboard/contracts/${id}/edit`)}
          >
            {t('editContract')}
          </Button>
          {contract.status === 'active' && (
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setTerminateDialog(true)}
            >
              {t('terminateContract')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Success/Error Messages */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      {/* Expiring Soon Warning */}
      {isExpiringSoon && (
        <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 3 }}>
          {t('expiringSoon')} - {daysRemaining} {t('days')}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Contract Information */}
        <Grid item xs={12} md={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('contractDuration')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography color="text.secondary" variant="body2">
                    {t('startDate')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatDate(contract.start_date)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography color="text.secondary" variant="body2">
                    {t('endDate')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatDate(contract.end_date)}
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom>
                {t('financialTerms')}
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} sm={3}>
                  <Typography color="text.secondary" variant="body2">
                    {t('rentAmount')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency(contract.rent_amount)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography color="text.secondary" variant="body2">
                    {t('securityDeposit')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {formatCurrency(contract.security_deposit)}
                  </Typography>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Typography color="text.secondary" variant="body2">
                    {t('paymentFrequency')}
                  </Typography>
                  <Typography variant="body1" fontWeight="medium">
                    {getPaymentFrequencyLabel(contract.payment_frequency)}
                  </Typography>
                </Grid>
              </Grid>

              {/* Contract Document */}
              {contract.contract_document_url && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Typography variant="h6" gutterBottom>
                    {t('contractDocument')}
                  </Typography>
                  <Button
                    variant="outlined"
                    startIcon={<DocumentIcon />}
                    href={contract.contract_document_url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t('viewDocument')}
                  </Button>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Property & Tenant Info */}
        <Grid item xs={12} md={4}>
          {/* Property Card */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <PropertyIcon color="primary" />
                <Typography variant="h6">{t('property')}</Typography>
              </Box>
              {contract.property ? (
                <>
                  <Typography variant="body1" fontWeight="medium">
                    {(contract.property as any).title || (contract.property as any).name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(contract.property as any).address}
                  </Typography>
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => navigate(`/dashboard/properties/${contract.property_id}`)}
                  >
                    {t('common:viewDetails')}
                  </Button>
                </>
              ) : (
                <Typography color="text.secondary">-</Typography>
              )}
            </CardContent>
          </Card>

          {/* Tenant Card */}
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <TenantIcon color="primary" />
                <Typography variant="h6">{t('tenant')}</Typography>
              </Box>
              {contract.tenant ? (
                <>
                  <Typography variant="body1" fontWeight="medium">
                    {(contract.tenant as any).first_name} {(contract.tenant as any).last_name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(contract.tenant as any).email}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {(contract.tenant as any).phone}
                  </Typography>
                  <Button
                    size="small"
                    sx={{ mt: 1 }}
                    onClick={() => navigate(`/dashboard/tenants/${contract.tenant_id}`)}
                  >
                    {t('common:viewDetails')}
                  </Button>
                </>
              ) : (
                <Typography color="text.secondary">-</Typography>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Terminate Contract Dialog */}
      <Dialog open={terminateDialog} onClose={() => setTerminateDialog(false)}>
        <DialogTitle>{t('terminateContract')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('terminateConfirmation')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTerminateDialog(false)}>{t('common:cancel')}</Button>
          <Button onClick={handleTerminate} color="error" variant="contained">
            {t('terminateContract')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
